/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, type ReactNode, createContext, forwardRef, useCallback, useEffect, useId, useRef, useState } from 'react';


import { useFocusVisible, useFocusWithin, useHover } from 'react-aria';
import { type ContextValue, DEFAULT_SLOT, Provider, TooltipTriggerStateContext, useContextProps } from 'react-aria-components';
import { CheckmarkStrokeIcon } from '../../icons/checkmark-stroke/checkmark-stroke.icon';
import { CloseStrokeIcon } from '../../icons/close-stroke/close-stroke.icon';
import { EditStrokeIcon } from '../../icons/edit-stroke/edit-stroke.icon';
import { MoreVerticalStrokeIcon } from '../../icons/more-vertical-stroke/more-vertical-stroke.icon';
import { $ } from '../../tokens.stylex';
import { BadgeContext } from '../badge/badge.component';
import { HintContext } from '../hint/hint.component';
import { IconButtonContext } from '../icon-button/icon-button.component';
import { IconContext } from '../icon/icon.component';
import { LabelContext } from '../label/label.component';
import { PopoverContext } from '../popover/popover.component';
import { SkeletonContext } from '../skeleton/skeleton.component';
import { TextContext } from '../text/text.component';
import { TooltipContext } from '../tooltip/tooltip.component';
import { View, ViewContext } from '../view/view.component';

interface InlineTableCustomFieldRenderProps {
  isEditing: boolean;
  isHovered: boolean;
  isFocusWithin: boolean;
  isFocusVisible: boolean;
  setEditing: (editing: boolean) => void;
  requestSubmit: () => void;
  requestReset: () => void;
}

interface InlineTableCustomFieldProps {
  style?: stylex.StyleXStyles;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isReadOnly?: boolean;
  children?: ReactNode | ((renderProps: InlineTableCustomFieldRenderProps) => ReactNode);
  onKeyDown?: React.KeyboardEventHandler;
  onKeyUp?: React.KeyboardEventHandler;
  onPointerEnter?: React.PointerEventHandler;
  onPointerLeave?: React.PointerEventHandler;
  variant?: 'primary' | 'warning' | 'success' | 'error';
}

const InlineTableCustomFieldContext = createContext<ContextValue<InlineTableCustomFieldProps, HTMLDivElement>>({});

const InlineTableCustomField = forwardRef((props: InlineTableCustomFieldProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, InlineTableCustomFieldContext);

  const { style, children, isDisabled, isInvalid, isReadOnly, variant } = props;

  const labelId = useId();
  const [isEditing, setIsEditing] = useState(isReadOnly || false);
  const [isFocusWithin, setFocusWithin] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [form, setForm] = useState<HTMLFormElement | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const actionButtonRef = useCallback((node: HTMLButtonElement | null) => setIsEditable(!!node), []);

  useEffect(() => {
    if (ref && typeof ref === 'object') {
      setForm(ref.current?.closest('form') ?? null);
    }
  }, [ref]);

  const { isFocusVisible } = useFocusVisible({});

  const { hoverProps, isHovered } = useHover({ isDisabled });

  const { focusWithinProps } = useFocusWithin({
    isDisabled,
    onFocusWithinChange: (isFocusWithin) => {
      setFocusWithin(isFocusWithin);
      // NOTE: Unlike TextField/NumberField, we do NOT auto-submit on blur.
      // The popover is portaled outside the DOM tree, so focusWithin fires false
      // immediately when the popover opens. The consumer calls requestSubmit()
      // when their popover closes.
    },
  });

  const requestSubmit = useCallback(() => {
    setIsEditing(false);
    form?.requestSubmit();
  }, [form]);

  const requestReset = useCallback(() => {
    setIsEditing(false);
    form?.reset();
  }, [form]);

  const renderProps: InlineTableCustomFieldRenderProps = {
    isEditing,
    isHovered,
    isFocusWithin,
    isFocusVisible,
    setEditing: setIsEditing,
    requestSubmit,
    requestReset,
  };

  return (
    <View
      ref={ref}
      onFocus={focusWithinProps.onFocus}
      onBlur={focusWithinProps.onBlur}
      style={[styles.base, style]}>
      <div
        role="group"
        aria-labelledby={labelId}
        data-hovered={isHovered || undefined}
        data-editing={isEditing || undefined}
        onPointerEnter={hoverProps.onPointerEnter}
        onPointerLeave={hoverProps.onPointerLeave}
        {...stylex.props(
          containerStyles.base,
          variant && containerStyles[variant],
          isEditing && containerStyles.editing,
          isEditing && isInvalid && containerStyles.invalid)}>
        <Provider values={[
          [LabelContext, { id: labelId, elementType: 'span', color: isHovered || isEditing || (isFocusWithin && isFocusVisible) ? 'primary' : 'tertiary', size: 14, style: labelStyles.base }],
          [HintContext, { style: [hintStyles.base, !isEditing && isInvalid && hintStyles.hidden] }],
          [TooltipContext, {
            isOpen: (isHovered || (isFocusWithin && isFocusVisible)) && !isEditing,
            triggerRef: contentRef as React.RefObject<Element>,
          }],
          [TooltipTriggerStateContext, {
            isOpen: (isHovered || (isFocusWithin && isFocusVisible)) && !isEditing,
            open() { /* controlled by isHovered */ },
            close() { /* controlled by isHovered */ },
          }],
          [BadgeContext, {
            isPastel: true,
            size: 20,
          }],
          [PopoverContext, {
            placement: "bottom right",
            offset: 5,
          }],
          [ViewContext, {
            slots: {
              [DEFAULT_SLOT]: {},
              [INLINE_TABLE_CUSTOM_FIELD_SLOTS.INPUT_VIEW]: {
                ref: contentRef,
                style: [viewStyles.inputView, isEditable && !isEditing && viewStyles.padded],
              }
            }
          }],
          [IconContext, {
            slots: {
              [DEFAULT_SLOT]: {},
              [INLINE_TABLE_CUSTOM_FIELD_SLOTS.INDICATOR_ICON]: {
                "aria-hidden": true,
                size: 16,
                style: indicatorStyles.base,
                ...(variant && variant !== 'primary' && { color: VARIANT_INDICATOR_COLOR[variant] }),
                ...(variant && variant !== 'primary' && { children: VARIANT_INDICATOR_ICON[variant] }),
              }
            }
          }],
          [TextContext, {
            slot: null!,
            size: 14,
            style: textStyles.base,
          }],
          [IconButtonContext, {
            slots: {
              [DEFAULT_SLOT]: {},
              [INLINE_TABLE_CUSTOM_FIELD_SLOTS.ACTION_BUTTON]: {
                ref: actionButtonRef,
                variant: 'ghostLowContrast',
                size: 32,
                "aria-label": "Actions",
                style: iconButtonStyles.actionButton,
                children: <MoreVerticalStrokeIcon />
              }
            }
          }],
          [SkeletonContext, { style: skeletonStyles.base }]
        ]}>
          {typeof children === 'function' ? children(renderProps) : children}
        </Provider>
      </div>
    </View>
  );
});

const styles = stylex.create({
  base: {
    borderRadius: 6,
    gridColumn: '1 / -1',
    alignItems: 'center',
    display: 'grid',
    gridTemplateColumns: 'subgrid',
  },
});

const containerStyles = stylex.create({
  base: {
    borderRadius: 4,
    gridColumn: '1 / -1',
    alignItems: 'flex-start',
    display: 'grid',
    gridTemplateColumns: 'subgrid',
    position: 'relative',
    minHeight: 36,
  },
  primary: {},
  warning: {
    backgroundColor: $.surfaceAttentiveSilent,
  },
  success: {
    backgroundColor: $.surfacePositiveSilent,
  },
  error: {
    backgroundColor: $.surfaceCriticalSilent,
  },
  editing: {
    backgroundColor: $.surfaceHighlightSilent,
  },
  invalid: {
    backgroundColor: $.surfaceCriticalSilent,
  }
});

const textStyles = stylex.create({
  base: {
    overflowWrap: 'break-word',
    minWidth: 0,
  },
});

const viewStyles = stylex.create({
  inputView: {
    gap: 4,
    overflow: 'hidden',
    alignItems: 'center',
    display: 'inline-flex',
    flexWrap: 'wrap',
    minHeight: 36,
    minWidth: 0,
    paddingBottom: 6,
    paddingInline: 8,
    paddingTop: 6,
  },
  padded: {
    paddingRight: 40,
  },
});

const labelStyles = stylex.create({
  base: {
    paddingInline: 8,
    alignItems: 'flex-start',
    transitionDuration: '0.2s',
    transitionProperty: 'color',
    transitionTimingFunction: 'easy-in-out',
    height: 36,
    paddingTop: 10,
  }
});

const iconButtonStyles = stylex.create({
  actionButton: {
    position: 'absolute',
    right: 2,
    top: 2,
  },
});

const hintStyles = stylex.create({
  base: {
    gridColumn: '2',
    paddingBottom: 4,
    paddingLeft: 8,
    paddingRight: 8,
  },
  hidden: {
    display: 'none',
  }
});

const skeletonStyles = stylex.create({
  base: {
    marginInline: 8,
    width: 'auto',
  },
});

const indicatorStyles = stylex.create({
  base: {
    marginLeft: 'auto',
    marginRight: 8,
  },
});

const VARIANT_INDICATOR_ICON = {
  warning: <EditStrokeIcon />,
  success: <CheckmarkStrokeIcon />,
  error: <CloseStrokeIcon />,
};

const VARIANT_INDICATOR_COLOR = {
  warning: 'attentive',
  success: 'positive',
  error: 'destructive',
} as const;

const INLINE_TABLE_CUSTOM_FIELD_SLOTS = {
  INPUT_VIEW: 'input-view',
  INDICATOR_ICON: 'indicator',
  ACTION_BUTTON: 'action-button',
};

export {
  INLINE_TABLE_CUSTOM_FIELD_SLOTS,
  InlineTableCustomField,
  InlineTableCustomFieldContext,
  type InlineTableCustomFieldProps,
  type InlineTableCustomFieldRenderProps
};

